import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Button, Form, Alert, Row, Col, Card, Badge, Spinner, Collapse } from 'react-bootstrap'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import {
  setTrainingSource,
  setValidationSource,
  selectSelectedTrainingSource,
  selectSelectedValidationSource,
} from '../../../store/slices/datasetSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import {
  useCreateModelMutation,
  useGetModelQuery,
  useTrainModelMutation,
  useStopTrainModelMutation,
  useUpdateModelMutation,
  useGetDefaultHyperparametersQuery,
  modelsApi,
} from '../../../services/modelsApi'
import { useGetContentMetadataQuery } from '../../../services/contentApi'
import { HyperParameterJSON } from '../../../types/models/hyperparameters'
import { ModelHyperparameters } from '../../../types/models/model'
import {
  Brain,
  Play,
  Square,
  Database,
  Settings,
  BarChart3,
  CheckCircle,
  Sparkles,
  Zap,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import SelectDatasetModal from '../../modals/SelectDatasetModal'
import HyperparameterSettingsModal from '../../modals/HyperparametersSettingsModal'
import ClassificationTrainingProgressCard from '../ModelTrainingView/ClassificationTrainingProgressCard'
import BBoxTrainingProgressCard from '../ModelTrainingView/BBoxTrainingProgressCard'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import NewModelModal from '../../modals/NewModelModal'
import styled from 'styled-components'

// Enhanced styled components for better visual design
const TrainingContainer = styled.div`
  background: transparent;
  border: none;
  border-radius: 12px;
  min-height: 100vh;
  padding: 2rem;
`

const HeaderSection = styled.div`
  margin-bottom: 2.5rem;

  .main-title {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 800;
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    color: #a0aec0;
    font-size: 1.1rem;
    font-weight: 400;
  }
`

const ConfigCard = styled(Card)`
  background: #1a202c;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
`

const OutputCard = styled(Card)`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  min-height: 600px;
`

const SectionTitle = styled.h6`
  color: #e2e8f0;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const InfoBox = styled.div`
  background: #2d3748;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: #374151;
  }
`

const SectionCard = styled.div<{ accentColor?: string }>`
  background: #2d3748;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid ${(props) => props.accentColor || '#4a5568'};
  border-radius: 10px;
  padding: 0;
  margin-bottom: 1.25rem;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    border-color: rgba(255, 255, 255, 0.12);
  }
`

const SectionHeader = styled.div<{ accentColor?: string }>`
  background: #2d3748;
  padding: 1rem 1.25rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: #374151;
  }
`

const SectionContent = styled.div`
  padding: 1.25rem 1.25rem;
`

const EmptyState = styled.div<{ accentColor?: string }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed ${(props) => props.accentColor || 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${(props) => props.accentColor || 'rgba(255, 255, 255, 0.3)'};
  }
`

const PrimaryButton = styled(Button).attrs({
  as: 'button',
})`
  background: #4a90e2;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
    background: #5ba0f2;
  }

  &:disabled {
    opacity: 0.5;
    transform: none;
    box-shadow: none;
    cursor: not-allowed;
  }
`

const SecondaryButton = styled(Button).attrs({
  as: 'button',
})`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.25);
    color: #ffffff;
    transform: translateY(-1px);
  }
`

const FormControl = styled(Form.Control)`
  background: #2d3748;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
  font-size: 0.9rem;

  &:focus {
    background: #374151;
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
    color: #ffffff;
  }

  &::placeholder {
    color: #718096;
  }

  &:hover:not(:focus) {
    border-color: rgba(255, 255, 255, 0.15);
    background: #374151;
  }
`

const StatusBadge = styled(Badge)`
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-weight: 500;
  font-size: 0.8rem;
`

const DatasetBadge = styled(Badge)`
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-weight: 500;
  font-size: 0.75rem;
  margin: 0.1rem;
`

const TrainingView: React.FC = () => {
  // This page is for creating NEW models OR continuing training on INITIALIZED models
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedTrainingSources = useAppSelector(selectSelectedTrainingSource)
  const selectedValidationSources = useAppSelector(selectSelectedValidationSource)

  // Local state for model selection - only used when navigating from initialized model
  const [localSelectedModelId, setLocalSelectedModelId] = useState<string | null>(null)

  // Get modelId from query parameters if navigating from an initialized model
  const urlParams = new URLSearchParams(location.search)
  const modelIdFromUrl = urlParams.get('modelId')

  // Training workflow state
  const [selectedBaseModel, setSelectedBaseModel] = useState<{ id: string; name: string } | null>(
    null
  )
  const [modelName, setModelName] = useState('')
  const [showBaseModelModal, setShowBaseModelModal] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [hyperparameters, setHyperparameters] = useState<any>(null)

  // Modal states
  const [showDatasetModal, setShowDatasetModal] = useState(false)
  const [showHyperparametersModal, setShowHyperparametersModal] = useState(false)
  const [showStopTrainingDialog, setShowStopTrainingDialog] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    baseModel: true,
    modelName: false,
    datasets: false,
    hyperparameters: false,
  })

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      // Accordion behavior: close all other sections, toggle the clicked one
      const newState: Record<string, boolean> = {
        baseModel: false,
        modelName: false,
        datasets: false,
        hyperparameters: false,
      }
      // If the clicked section was already open, close it. Otherwise, open it.
      newState[section] = !prev[section]
      return newState
    })
  }

  // Dataset selection state
  const [datasetModalType, setDatasetModalType] = useState<'training' | 'validation'>('training')
  const [isModelTraining, setIsModelTraining] = useState(false)
  const [isStartingTraining, setIsStartingTraining] = useState(false)

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const { data: selectedModel, isLoading: isLoadingModel } = useGetModelQuery(
    localSelectedModelId || '',
    {
      skip: !localSelectedModelId || !projectId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      pollingInterval: 0, // Disable polling temporarily to test if it's causing logout
    }
  )

  // Check which sections are configured
  const isBaseModelConfigured = !!(
    selectedBaseModel ||
    (selectedModel && selectedModel.state === 'INITIALIZED')
  )
  const isModelNameConfigured = !!modelName.trim()
  const isDatasetsConfigured = !!(selectedTrainingSources?.length > 0)
  const isHyperparametersConfigured = !!hyperparameters

  // Get customized hyperparameters (those that differ from defaults)
  // This matches the defaults used in HyperparametersSettingsModal
  const getCustomizedHyperparameters = (): string[] => {
    if (!hyperparameters?.hyperparameters || !project?.annotation_type) return []

    const customized: string[] = []
    const current = hyperparameters.hyperparameters
    const isObjectDetection =
      project.annotation_type === 'bounding_box' ||
      project.annotation_type === 'oriented_bounding_box'

    // Get default values matching HyperparametersSettingsModal
    const getDefaultValue = (paramName: string): any => {
      if (isObjectDetection) {
        const objDetDefaults: Record<string, any> = {
          epochs: 10,
          learning_rate: 0.01,
          batch_size: 16,
          momentum: 0.937,
          weight_decay: 0.0005,
          iou_threshold: 0.5,
          conf_threshold: 0.25,
        }
        return objDetDefaults[paramName]
      } else {
        // Classification defaults
        const classificationDefaults: Record<string, any> = {
          epochs: 10,
          learning_rate: 0.001,
          batch_size: 32,
          optimizer: 'adam',
          weight_decay: 0.0001,
          early_stopping: true,
          early_stopping_patience: 5,
        }
        return classificationDefaults[paramName]
      }
    }

    // Get default transform values (matching HyperparametersSettingsModal)
    const getDefaultTransformValue = (transformName: string, paramKey: string): any => {
      if (isObjectDetection) {
        const objDetTransformDefaults: Record<string, Record<string, any>> = {
          mosaic: { enabled: true },
          mixup: { enabled: false },
          copy_paste: { enabled: false },
          hsv_h: { value: 0.015 },
          hsv_s: { value: 0.7 },
          hsv_v: { value: 0.4 },
        }
        return objDetTransformDefaults[transformName]?.[paramKey]
      } else {
        // Classification transform defaults (matching modal)
        const classificationTransformDefaults: Record<string, Record<string, any>> = {
          random_brightness_contrast: { enabled: true },
          random_flip: { enabled: true },
          random_rotation: { enabled: false },
          normalize: { enabled: true },
        }
        return classificationTransformDefaults[transformName]?.[paramKey]
      }
    }

    // Check training parameters
    // Only check parameters that have defaults defined (to avoid showing all params)
    if (current.training) {
      const defaultParamNames = isObjectDetection
        ? [
            'epochs',
            'learning_rate',
            'batch_size',
            'momentum',
            'weight_decay',
            'iou_threshold',
            'conf_threshold',
          ]
        : [
            'epochs',
            'learning_rate',
            'batch_size',
            'optimizer',
            'weight_decay',
            'early_stopping',
            'early_stopping_patience',
          ]

      defaultParamNames.forEach((key) => {
        const param = current.training[key]
        if (param && typeof param === 'object' && param.value !== undefined) {
          const defaultValue = getDefaultValue(key)
          if (defaultValue !== undefined) {
            // Handle numeric comparison with tolerance for floating point
            let isDifferent = false
            if (typeof param.value === 'number' && typeof defaultValue === 'number') {
              const tolerance = 0.0001
              isDifferent = Math.abs(param.value - defaultValue) > tolerance
            } else {
              isDifferent = param.value !== defaultValue
            }

            if (isDifferent) {
              const displayName = key
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
              customized.push(displayName)
            }
          }
        }
      })
    }

    // Check transforms
    if (current.transforms) {
      Object.entries(current.transforms).forEach(
        ([transformName, transformParams]: [string, any]) => {
          if (transformParams && typeof transformParams === 'object') {
            Object.entries(transformParams).forEach(([paramKey, paramValue]: [string, any]) => {
              if (paramValue && typeof paramValue === 'object' && paramValue.value !== undefined) {
                const defaultValue = getDefaultTransformValue(transformName, paramKey)
                if (defaultValue !== undefined && paramValue.value !== defaultValue) {
                  if (paramKey === 'enabled') {
                    const displayName = transformName
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')
                    customized.push(
                      paramValue.value ? `${displayName} (enabled)` : `${displayName} (disabled)`
                    )
                  } else {
                    const displayName = `${transformName
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')} ${paramKey
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}`
                    customized.push(displayName)
                  }
                }
              }
            })
          }
        }
      )
    }

    return customized
  }

  const customizedHyperparameters = getCustomizedHyperparameters()

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: projectId || '',
    },
    { skip: !projectId }
  )

  const [createModel, { isLoading: isCreatingModel, error: createModelError }] =
    useCreateModelMutation()
  const [updateModel] = useUpdateModelMutation()
  const [trainModel] = useTrainModelMutation()
  const [stopTrainModel, { isLoading: isStoppingTraining }] = useStopTrainModelMutation()

  // Base models catalog
  const baseModels = [{ id: 'baseModel1', name: 'AG Base', image: '/agileview-eye.png' }]

  // Redirect to projects if no project selected
  useEffect(() => {
    if (!selectedProjectId && !projectId) {
      navigate('/projects')
    }
  }, [selectedProjectId, projectId, navigate])

  // Clear any selected model when entering training page - this is for NEW models only
  useEffect(() => {
    if (projectId) {
      // Clear local model selection instead of global state
      setLocalSelectedModelId(null)
      // Also clear any base model selection
      setSelectedBaseModel(null)
      setModelName('')
      setHyperparameters(null)
      dispatch(setTrainingSource([]))
      dispatch(setValidationSource([]))
    }
  }, [projectId, dispatch])

  // Handle model selection based on URL parameters or clear for new models
  useEffect(() => {
    if (modelIdFromUrl && projectId) {
      // If we have a modelId from URL (navigating from initialized model), set it locally
      setLocalSelectedModelId(modelIdFromUrl)
    } else {
      // Clear local model selection for new models
      setLocalSelectedModelId(null)
    }
  }, [modelIdFromUrl, projectId])

  // Update training sources when model changes - only if explicitly requested
  // This prevents automatic dataset population when navigating from other pages
  useEffect(() => {
    if (selectedModel && modelIdFromUrl) {
      // Only populate datasets when explicitly navigating from an initialized model
      dispatch(setTrainingSource(selectedModel.parameters?.train_metadata?.datasource || []))
      dispatch(setValidationSource(selectedModel.parameters?.validation_metadata?.datasource || []))

      // Set the base model selection for existing initialized models
      // Since there's only one base model option, set it to the default
      if (!selectedBaseModel && baseModels.length > 0) {
        setSelectedBaseModel(baseModels[0])
      }

      // Also set the model name if it's not already set
      if (selectedModel.name && !modelName.trim()) {
        setModelName(selectedModel.name)
      }

      // Load hyperparameters from the model if they exist
      // selectedModel.parameters.hyperparameters is already the full HyperParameterJSON structure
      if (selectedModel.parameters?.hyperparameters && !hyperparameters) {
        setHyperparameters(selectedModel.parameters.hyperparameters)
      }
    }
  }, [selectedModel, modelIdFromUrl, dispatch, selectedBaseModel, modelName, hyperparameters])

  // Update training state when model state changes
  useEffect(() => {
    if (selectedModel?.state?.toLowerCase() === 'training') {
      setIsModelTraining(true)
    } else {
      setIsModelTraining(false)
    }
  }, [selectedModel?.state])

  // Reset page when training completes
  useEffect(() => {
    if (selectedModel?.state === 'TRAINED' && isModelTraining) {
      // Only reset if we were actually training this model
      setTimeout(() => {
        handleResetPage()
      }, 3000)
    }
  }, [selectedModel?.state, isModelTraining])

  const handleResetPage = () => {
    // Reset everything for a fresh start
    setLocalSelectedModelId(null)
    setSelectedBaseModel(null)
    setModelName('')
    setHyperparameters(null)
    dispatch(setTrainingSource([]))
    dispatch(setValidationSource([]))
    setIsModelTraining(false)
    setIsStartingTraining(false)
  }

  // Sanitize model name for AWS SageMaker compatibility
  // SageMaker job names must match: [a-zA-Z0-9](-*[a-zA-Z0-9]){0,31}
  const sanitizeModelNameForSageMaker = (name: string): string => {
    // Replace underscores and spaces with hyphens
    let sanitized = name.replace(/[_\s]+/g, '-')
    // Remove any other invalid characters (keep only alphanumeric and hyphens)
    sanitized = sanitized.replace(/[^a-zA-Z0-9-]/g, '')
    // Remove consecutive hyphens
    sanitized = sanitized.replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    sanitized = sanitized.replace(/^-+|-+$/g, '')
    // Ensure it starts with alphanumeric
    if (!/^[a-zA-Z0-9]/.test(sanitized)) {
      sanitized = 'model-' + sanitized
    }
    // Truncate to 32 characters max (SageMaker limit)
    if (sanitized.length > 32) {
      sanitized = sanitized.substring(0, 32)
      // Ensure it doesn't end with a hyphen after truncation
      sanitized = sanitized.replace(/-+$/, '')
    }
    return sanitized || 'model'
  }

  const handleBaseModelSelect = async (baseModel: { id: string; name: string }) => {
    try {
      setSelectedBaseModel(baseModel)
      setShowBaseModelModal(false)
      // Don't create the model yet - wait until user clicks "Start Training" or "Finish Later"
    } catch (error: any) {
      console.error('Base model selection error:', error)
      setErrorMessage('Failed to select base model')
      setShowError(true)
    }
  }

  const createModelWithConfig = async (startTraining = false) => {
    if (!selectedBaseModel || !projectId) {
      setErrorMessage('Please select a base model first')
      setShowError(true)
      return
    }

    setIsStartingTraining(true)

    try {
      // Create the model with all current configuration
      const rawModelName = modelName.trim() || `${selectedBaseModel.name}-${Date.now()}`
      const modelNameToUse = sanitizeModelNameForSageMaker(rawModelName)

      // Warn user if name was changed
      if (rawModelName !== modelNameToUse && modelName.trim()) {
        console.warn(
          `Model name sanitized from "${rawModelName}" to "${modelNameToUse}" for AWS SageMaker compatibility`
        )
      }
      const newModel = await createModel({
        name: modelNameToUse,
        project_id: projectId,
        description: `AI model based on ${selectedBaseModel.name} for ${
          project?.annotation_type || 'classification'
        } tasks`,
      }).unwrap()

      // Update the model with hyperparameters if provided
      // IMPORTANT: Hyperparameters must be saved before training starts
      // Backend requires ALL object detection training hyperparameters to be present
      if (hyperparameters) {
        try {
          // Fetch API defaults from the newly created model to ensure all required fields are present
          let apiDefaults: any = null
          try {
            const defaultsResult = await dispatch(
              modelsApi.endpoints.getDefaultHyperparameters.initiate(newModel.id)
            )
            if ('data' in defaultsResult && defaultsResult.data) {
              apiDefaults = defaultsResult.data
              console.log('API defaults fetched:', apiDefaults)
            } else {
              console.warn('API defaults query returned no data:', defaultsResult)
            }
          } catch (fetchError) {
            console.error('Could not fetch API defaults:', fetchError)
          }

          // Merge API defaults with user hyperparameters to ensure all required fields are present
          // Backend expects: { type: 'object_detection'|'classification', hyperparameters: {training: {...}, transforms: {...}} }
          // Modal returns: { hyperparameters: { type, hyperparameters: {training: {...}, transforms: {...}} } }
          // So hyperparameters state is: { hyperparameters: ModelHyperparameters }
          // Extract user's configured values - need to go deep to get the actual training/transforms
          const userHyperparams =
            hyperparameters?.hyperparameters?.hyperparameters ||
            hyperparameters?.hyperparameters ||
            hyperparameters
          const hyperparametersType =
            hyperparameters?.hyperparameters?.type ||
            hyperparameters?.type ||
            (project?.annotation_type === 'bounding_box' ||
            project?.annotation_type === 'oriented_bounding_box'
              ? 'object_detection'
              : 'classification')

          console.log('Full hyperparameters state:', hyperparameters)
          console.log('User hyperparameters extracted (before merge):', userHyperparams)
          console.log('User epochs value:', userHyperparams?.training?.epochs?.value)
          console.log('API defaults structure:', apiDefaults)
          console.log(
            'API defaults epochs value:',
            apiDefaults?.hyperparameters?.training?.epochs?.value ||
              apiDefaults?.training?.epochs?.value
          )
          console.log('Hyperparameters type:', hyperparametersType)

          let mergedHyperparams: any
          if (apiDefaults?.hyperparameters) {
            // API returned defaults wrapped in hyperparameters - merge them with user values
            // User values should override API defaults (spread user values last)
            mergedHyperparams = {
              training: {
                ...(apiDefaults.hyperparameters.training || {}),
                ...(userHyperparams.training || {}),
              },
              transforms: {
                ...(apiDefaults.hyperparameters.transforms || {}),
                ...(userHyperparams.transforms || {}),
              },
            }
          } else if (apiDefaults && (apiDefaults.training || apiDefaults.transforms)) {
            // API returned defaults directly as {training: {...}, transforms: {...}} - merge with user's hyperparameters
            // User values should override API defaults (spread user values last)
            mergedHyperparams = {
              training: {
                ...(apiDefaults.training || {}),
                ...(userHyperparams.training || {}),
              },
              transforms: {
                ...(apiDefaults.transforms || {}),
                ...(userHyperparams.transforms || {}),
              },
            }
          } else {
            // No API defaults - use user's hyperparameters (will likely fail for object detection)
            console.warn(
              'No API defaults available, using user hyperparameters only. This may fail for object detection.'
            )
            mergedHyperparams = userHyperparams
          }

          console.log(
            'Merged hyperparameters (user values should override defaults):',
            mergedHyperparams
          )
          console.log('Merged epochs value:', mergedHyperparams?.training?.epochs?.value)

          // Wrap in the structure the backend expects: { type, hyperparameters: {...} }
          const finalHyperparameters = {
            type: hyperparametersType,
            hyperparameters: mergedHyperparams,
          }
          console.log('Final hyperparameters (with type wrapper):', finalHyperparameters)

          const updatedModel = await updateModel({
            id: newModel.id,
            modelData: {
              name: modelNameToUse,
              description: newModel.description,
              parameters: {
                ...newModel.parameters,
                // Use the merged hyperparameters with all required fields
                // Backend expects: { type: 'object_detection'|'classification', hyperparameters: {...} }
                hyperparameters: finalHyperparameters,
              },
            },
          }).unwrap()
          // Use the updated model to ensure hyperparameters are saved
          console.log('Hyperparameters saved to model:', updatedModel.parameters?.hyperparameters)
        } catch (updateError: any) {
          console.error('Failed to update model with hyperparameters:', updateError)
          const errorMessage =
            updateError?.data?.message ||
            updateError?.message ||
            'Failed to save hyperparameters. Please try again.'
          setErrorMessage(errorMessage)
          setShowError(true)
          setIsStartingTraining(false)
          return // Don't continue if hyperparameters fail to save
        }
      }

      // Set the newly created model as selected locally
      setLocalSelectedModelId(newModel.id)

      // If startTraining is true, start training immediately
      // The backend will read hyperparameters from model.parameters.hyperparameters
      if (startTraining && selectedTrainingSources?.length > 0) {
        await trainModel({
          id: newModel.id,
          train_metadata_filter:
            selectedTrainingSources.length > 0
              ? { datasource: selectedTrainingSources }
              : undefined,
          validation_metadata_filter:
            selectedValidationSources.length > 0
              ? { datasource: selectedValidationSources }
              : undefined,
        }).unwrap()
      }
    } catch (error: any) {
      setErrorMessage(error?.data?.message || 'Failed to create model')
      setShowError(true)
    } finally {
      setIsStartingTraining(false)
    }
  }

  const handleFinishLater = async () => {
    await createModelWithConfig(false)
  }

  const handleStartTraining = async () => {
    if (!selectedBaseModel) {
      setErrorMessage('Please select a base model first')
      setShowError(true)
      return
    }

    if (!selectedTrainingSources?.length) {
      setErrorMessage('Please select at least one training dataset')
      setShowError(true)
      return
    }

    // Check if user is authenticated
    const accessToken = localStorage.getItem('access_token')
    if (!accessToken) {
      setErrorMessage('You are not logged in. Please log in again.')
      setShowError(true)
      return
    }

    // If model already exists (continuing from initialized model), update and start training
    if (localSelectedModelId && selectedModel) {
      setIsStartingTraining(true)

      try {
        // Update the existing model with name and hyperparameters if changed
        const nameToUse = modelName.trim() || selectedModel.name
        const nameChanged = modelName.trim() && modelName.trim() !== selectedModel.name
        const hasHyperparameters = !!hyperparameters
        const needsUpdate = nameChanged || hasHyperparameters

        if (needsUpdate) {
          try {
            // If updating hyperparameters, fetch API defaults to ensure all required fields are present
            let finalHyperparameters = hyperparameters?.hyperparameters || hyperparameters
            if (hasHyperparameters && hyperparameters) {
              try {
                const defaultsResult = await dispatch(
                  modelsApi.endpoints.getDefaultHyperparameters.initiate(localSelectedModelId)
                )
                if ('data' in defaultsResult && defaultsResult.data) {
                  const apiDefaults = defaultsResult.data
                  console.log('API defaults fetched for existing model:', apiDefaults)

                  const hyperparametersType =
                    hyperparameters.type ||
                    (project?.annotation_type === 'bounding_box' ||
                    project?.annotation_type === 'oriented_bounding_box'
                      ? 'object_detection'
                      : 'classification')

                  // Extract user's hyperparameters correctly
                  // Modal returns: { hyperparameters: { type, hyperparameters: {training: {...}, transforms: {...}} } }
                  const userHyperparams =
                    hyperparameters?.hyperparameters?.hyperparameters ||
                    hyperparameters?.hyperparameters ||
                    hyperparameters

                  let mergedHyperparams: any
                  if (apiDefaults.hyperparameters) {
                    // API returned defaults wrapped in hyperparameters
                    // User values should override API defaults
                    mergedHyperparams = {
                      training: {
                        ...(apiDefaults.hyperparameters.training || {}),
                        ...(userHyperparams.training || {}),
                      },
                      transforms: {
                        ...(apiDefaults.hyperparameters.transforms || {}),
                        ...(userHyperparams.transforms || {}),
                      },
                    }
                  } else if (apiDefaults.training || apiDefaults.transforms) {
                    // API returned defaults directly as {training: {...}, transforms: {...}}
                    // User values should override API defaults
                    mergedHyperparams = {
                      training: {
                        ...(apiDefaults.training || {}),
                        ...(userHyperparams.training || {}),
                      },
                      transforms: {
                        ...(apiDefaults.transforms || {}),
                        ...(userHyperparams.transforms || {}),
                      },
                    }
                  } else {
                    console.warn('API defaults have unexpected structure:', apiDefaults)
                    mergedHyperparams = userHyperparams
                  }

                  console.log('User hyperparameters:', userHyperparams)
                  console.log(
                    'Merged hyperparameters (user values should override defaults):',
                    mergedHyperparams
                  )

                  // Wrap in the structure the backend expects: { type, hyperparameters: {...} }
                  finalHyperparameters = {
                    type: hyperparametersType,
                    hyperparameters: mergedHyperparams,
                  }
                  console.log(
                    'Final hyperparameters (merged with API defaults):',
                    finalHyperparameters
                  )
                } else {
                  console.warn('API defaults query returned no data:', defaultsResult)
                  // Still wrap with type even if no API defaults
                  const hyperparametersType =
                    hyperparameters.type ||
                    (project?.annotation_type === 'bounding_box' ||
                    project?.annotation_type === 'oriented_bounding_box'
                      ? 'object_detection'
                      : 'classification')
                  finalHyperparameters = {
                    type: hyperparametersType,
                    hyperparameters: hyperparameters.hyperparameters || hyperparameters,
                  }
                }
              } catch (fetchError) {
                console.error('Could not fetch API defaults:', fetchError)
                // Still wrap with type even if fetch fails
                const hyperparametersType =
                  hyperparameters?.hyperparameters?.type ||
                  hyperparameters?.type ||
                  (project?.annotation_type === 'bounding_box' ||
                  project?.annotation_type === 'oriented_bounding_box'
                    ? 'object_detection'
                    : 'classification')
                const userHyperparams =
                  hyperparameters?.hyperparameters?.hyperparameters ||
                  hyperparameters?.hyperparameters ||
                  hyperparameters
                finalHyperparameters = {
                  type: hyperparametersType,
                  hyperparameters: userHyperparams,
                }
              }
            }

            const updatedModel = await updateModel({
              id: localSelectedModelId,
              modelData: {
                name: nameToUse,
                description: selectedModel.description,
                parameters: {
                  ...selectedModel.parameters,
                  ...(finalHyperparameters ? { hyperparameters: finalHyperparameters } : {}),
                },
              },
            }).unwrap()
            console.log(
              'Model updated with hyperparameters:',
              updatedModel.parameters?.hyperparameters
            )
          } catch (updateError: any) {
            console.error('Failed to update model name/hyperparameters:', updateError)
            // For hyperparameters, we should fail if they don't save
            if (hasHyperparameters) {
              const errorMessage =
                updateError?.data?.message ||
                updateError?.message ||
                'Failed to save hyperparameters. Please try again.'
              setErrorMessage(errorMessage)
              setShowError(true)
              setIsStartingTraining(false)
              return
            }
            // Only continue if it was just a name change that failed
            console.warn('Failed to update model name, continuing with training:', updateError)
          }
        }

        // Start training on the existing initialized model
        // The backend will read hyperparameters from model.parameters.hyperparameters
        await trainModel({
          id: localSelectedModelId,
          train_metadata_filter:
            selectedTrainingSources.length > 0
              ? { datasource: selectedTrainingSources }
              : undefined,
          validation_metadata_filter:
            selectedValidationSources.length > 0
              ? { datasource: selectedValidationSources }
              : undefined,
        }).unwrap()
      } catch (error: any) {
        setErrorMessage(error?.data?.message || 'Failed to start training')
        setShowError(true)
      } finally {
        setIsStartingTraining(false)
      }
    } else {
      // Create new model and start training
      await createModelWithConfig(true)
    }
  }

  const handleStopTraining = async () => {
    if (selectedModel?.id) {
      try {
        await stopTrainModel({ id: selectedModel.id }).unwrap()
        setShowStopTrainingDialog(false)
      } catch (error: any) {
        setErrorMessage(error?.data?.message || 'Failed to stop training')
        setShowError(true)
      }
    }
  }

  const handleDatasetSelect = (type: 'training' | 'validation') => {
    setDatasetModalType(type)
    setShowDatasetModal(true)
  }

  const handleAcceptDatasetSelections = (sources: string[]) => {
    if (datasetModalType === 'training') {
      dispatch(setTrainingSource(sources))
    } else {
      dispatch(setValidationSource(sources))
    }
    setShowDatasetModal(false)
  }

  const canStartTraining =
    (selectedModel &&
      selectedModel.state === 'INITIALIZED' &&
      selectedTrainingSources?.length > 0) ||
    (selectedBaseModel &&
      selectedTrainingSources?.length > 0 &&
      !isModelTraining &&
      !isStartingTraining)

  const canFinishLater =
    selectedBaseModel && !localSelectedModelId && !isModelTraining && !isStartingTraining
  const availableDatasets = metadata?.metadata?.datasource || []

  return (
    <TrainingContainer>
      {/* Enhanced Header */}
      <HeaderSection>
        <h1 className="main-title">
          <Sparkles
            size={32}
            className="me-3"
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
          Train Vision Model
        </h1>
        <p className="subtitle">
          Create and configure your AI model for{' '}
          <span style={{ color: '#667eea', fontWeight: '600' }}>
            {project?.annotation_type
              ?.replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Classification'}
          </span>{' '}
          tasks
        </p>
      </HeaderSection>

      <Row className="g-4">
        {/* Left Panel - Training Configuration */}
        <Col lg={4}>
          <div className="d-flex flex-column h-100">
            {/* Training Configuration Section */}
            <ConfigCard className="flex-grow-1">
              <Card.Body className="d-flex flex-column p-4">
                <div className="mb-4">
                  <h4
                    className="text-white mb-2"
                    style={{
                      fontWeight: '600',
                      fontSize: '1.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <Zap size={20} style={{ color: '#4a90e2' }} />
                    {selectedModel && selectedModel.state === 'INITIALIZED'
                      ? 'Continue Training'
                      : 'Training Configuration'}
                  </h4>
                  <p
                    className="mb-0"
                    style={{ color: '#a0aec0', fontSize: '0.875rem', lineHeight: '1.5' }}
                  >
                    {selectedModel && selectedModel.state === 'INITIALIZED'
                      ? `Continue training "${selectedModel.name}" with your selected datasets.`
                      : 'Select a base model, configure datasets, and start training.'}
                  </p>
                </div>

                {createModelError && (
                  <Alert variant="danger" className="mb-4">
                    Failed to create model. Please try again.
                  </Alert>
                )}

                {/* Model Information or Base Model Selection */}
                <SectionCard accentColor="#4a90e2">
                  <SectionHeader accentColor="#4a90e2" onClick={() => toggleSection('baseModel')}>
                    <SectionTitle style={{ marginBottom: 0 }}>
                      <Brain size={14} style={{ color: '#4a90e2' }} />
                      {selectedModel && selectedModel.state === 'INITIALIZED'
                        ? 'Model Information'
                        : 'Base Model'}
                      {isBaseModelConfigured && (
                        <CheckCircle size={14} style={{ color: '#48bb78', marginLeft: '0.5rem' }} />
                      )}
                    </SectionTitle>
                    {expandedSections.baseModel ? (
                      <ChevronUp size={18} style={{ color: '#a0aec0' }} />
                    ) : (
                      <ChevronDown size={18} style={{ color: '#a0aec0' }} />
                    )}
                  </SectionHeader>
                  <Collapse in={expandedSections.baseModel}>
                    <SectionContent>
                      {selectedModel && selectedModel.state === 'INITIALIZED' ? (
                        <InfoBox className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              background: '#4a90e2',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src="/agileview-eye.png"
                              alt={selectedModel.name}
                              width="20"
                              height="20"
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div
                              className="text-white"
                              style={{ fontWeight: '600', fontSize: '0.95rem' }}
                            >
                              {selectedModel.name}
                            </div>
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <StatusBadge bg="warning">Initialized</StatusBadge>
                              <small style={{ color: '#a0aec0', fontSize: '0.75rem' }}>
                                {new Date(selectedModel.created_at).toLocaleDateString()}
                              </small>
                            </div>
                          </div>
                        </InfoBox>
                      ) : (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <small
                              style={{ color: '#a0aec0', fontSize: '0.8rem', fontWeight: '500' }}
                            >
                              {selectedBaseModel
                                ? 'Base model selected'
                                : 'Select a base model to begin'}
                            </small>
                            <SecondaryButton
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowBaseModelModal(true)
                              }}
                              size="sm"
                            >
                              <Brain size={12} className="me-1" />
                              {selectedBaseModel ? 'Change' : 'Select'}
                            </SecondaryButton>
                          </div>
                          {selectedBaseModel ? (
                            <div className="mt-2">
                              <InfoBox className="d-flex align-items-center gap-3">
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    background: '#4a90e2',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <img
                                    src="/agileview-eye.png"
                                    alt={selectedBaseModel.name}
                                    width="20"
                                    height="20"
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <div
                                    className="text-white"
                                    style={{ fontWeight: '600', fontSize: '0.95rem' }}
                                  >
                                    {selectedBaseModel.name}
                                  </div>
                                  {selectedModel && (
                                    <small style={{ color: '#a0aec0', fontSize: '0.75rem' }}>
                                      Model: {selectedModel.name}
                                    </small>
                                  )}
                                </div>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                  style={{ color: '#a0aec0', fontSize: '1.2rem', lineHeight: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedBaseModel(null)
                                    setLocalSelectedModelId(null)
                                  }}
                                >
                                  ×
                                </Button>
                              </InfoBox>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <EmptyState accentColor="#4a90e2">
                                <Brain
                                  size={24}
                                  style={{ color: '#4a90e2', opacity: 0.6, marginBottom: '0.5rem' }}
                                />
                                <small style={{ color: '#a0aec0', fontSize: '0.875rem' }}>
                                  No base model selected
                                </small>
                              </EmptyState>
                            </div>
                          )}
                        </>
                      )}
                    </SectionContent>
                  </Collapse>
                </SectionCard>

                {/* Model Name */}
                <SectionCard accentColor="#9f7aea">
                  <SectionHeader accentColor="#9f7aea" onClick={() => toggleSection('modelName')}>
                    <SectionTitle style={{ marginBottom: 0 }}>
                      <Sparkles size={14} style={{ color: '#9f7aea' }} />
                      Model Name
                      {isModelNameConfigured && (
                        <CheckCircle size={14} style={{ color: '#48bb78', marginLeft: '0.5rem' }} />
                      )}
                    </SectionTitle>
                    {expandedSections.modelName ? (
                      <ChevronUp size={18} style={{ color: '#a0aec0' }} />
                    ) : (
                      <ChevronDown size={18} style={{ color: '#a0aec0' }} />
                    )}
                  </SectionHeader>
                  <Collapse in={expandedSections.modelName}>
                    <SectionContent>
                      <FormControl
                        type="text"
                        placeholder="Enter custom model name..."
                        value={modelName}
                        onChange={(e) => setModelName(e.target.value)}
                        maxLength={50}
                      />
                      <Form.Text
                        style={{
                          color: '#718096',
                          fontSize: '0.75rem',
                          marginTop: '0.5rem',
                          display: 'block',
                        }}
                      >
                        Leave empty to auto-generate from base model
                        {modelName.trim() &&
                          modelName.trim() !== sanitizeModelNameForSageMaker(modelName.trim()) && (
                            <span
                              style={{ color: '#ed8936', display: 'block', marginTop: '0.25rem' }}
                            >
                              Note: Name will be sanitized for AWS compatibility (underscores/spaces
                              → hyphens)
                            </span>
                          )}
                      </Form.Text>
                    </SectionContent>
                  </Collapse>
                </SectionCard>

                {/* Dataset Selection */}
                <SectionCard accentColor="#48bb78">
                  <SectionHeader accentColor="#48bb78" onClick={() => toggleSection('datasets')}>
                    <SectionTitle style={{ marginBottom: 0 }}>
                      <Database size={14} style={{ color: '#48bb78' }} />
                      Training Datasets
                      {isDatasetsConfigured && (
                        <CheckCircle size={14} style={{ color: '#48bb78', marginLeft: '0.5rem' }} />
                      )}
                    </SectionTitle>
                    {expandedSections.datasets ? (
                      <ChevronUp size={18} style={{ color: '#a0aec0' }} />
                    ) : (
                      <ChevronDown size={18} style={{ color: '#a0aec0' }} />
                    )}
                  </SectionHeader>
                  <Collapse in={expandedSections.datasets}>
                    <SectionContent>
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <small
                            style={{ color: '#a0aec0', fontSize: '0.8rem', fontWeight: '500' }}
                          >
                            Training Dataset
                          </small>
                          <SecondaryButton
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDatasetSelect('training')
                            }}
                            size="sm"
                          >
                            <Database size={12} className="me-1" />
                            Select
                          </SecondaryButton>
                        </div>
                        {selectedTrainingSources?.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {selectedTrainingSources.map((source, index) => (
                              <DatasetBadge key={index} bg="success">
                                {source}
                              </DatasetBadge>
                            ))}
                          </div>
                        ) : (
                          <EmptyState accentColor="#48bb78">
                            <Database
                              size={20}
                              style={{ color: '#48bb78', opacity: 0.6, marginBottom: '0.5rem' }}
                            />
                            <small style={{ color: '#a0aec0', fontSize: '0.875rem' }}>
                              No training dataset selected
                            </small>
                          </EmptyState>
                        )}
                      </div>

                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <small
                            style={{ color: '#a0aec0', fontSize: '0.8rem', fontWeight: '500' }}
                          >
                            Validation Dataset (Optional)
                          </small>
                          <SecondaryButton
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDatasetSelect('validation')
                            }}
                            size="sm"
                          >
                            <Database size={12} className="me-1" />
                            Select
                          </SecondaryButton>
                        </div>
                        {selectedValidationSources?.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {selectedValidationSources.map((source, index) => (
                              <DatasetBadge key={index} bg="info">
                                {source}
                              </DatasetBadge>
                            ))}
                          </div>
                        ) : (
                          <EmptyState accentColor="#48bb78">
                            <Database
                              size={20}
                              style={{ color: '#48bb78', opacity: 0.6, marginBottom: '0.5rem' }}
                            />
                            <small style={{ color: '#a0aec0', fontSize: '0.875rem' }}>
                              No validation dataset selected
                            </small>
                          </EmptyState>
                        )}
                      </div>
                    </SectionContent>
                  </Collapse>
                </SectionCard>

                {/* Hyperparameters */}
                <SectionCard accentColor="#ed8936">
                  <SectionHeader
                    accentColor="#ed8936"
                    onClick={() => toggleSection('hyperparameters')}
                  >
                    <SectionTitle style={{ marginBottom: 0 }}>
                      <Settings size={14} style={{ color: '#ed8936' }} />
                      Hyperparameters
                      {isHyperparametersConfigured && (
                        <CheckCircle size={14} style={{ color: '#48bb78', marginLeft: '0.5rem' }} />
                      )}
                    </SectionTitle>
                    {expandedSections.hyperparameters ? (
                      <ChevronUp size={18} style={{ color: '#a0aec0' }} />
                    ) : (
                      <ChevronDown size={18} style={{ color: '#a0aec0' }} />
                    )}
                  </SectionHeader>
                  <Collapse in={expandedSections.hyperparameters}>
                    <SectionContent>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <small style={{ color: '#a0aec0', fontSize: '0.8rem', fontWeight: '500' }}>
                          Training Parameters
                        </small>
                        <SecondaryButton
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowHyperparametersModal(true)
                          }}
                          size="sm"
                        >
                          <Settings size={12} className="me-1" />
                          {hyperparameters ? 'Edit' : 'Configure'}
                        </SecondaryButton>
                      </div>
                      <div>
                        {hyperparameters ? (
                          customizedHyperparameters.length > 0 ? (
                            <div>
                              <small
                                style={{
                                  color: '#a0aec0',
                                  fontSize: '0.75rem',
                                  display: 'block',
                                  marginBottom: '0.75rem',
                                }}
                              >
                                Customized parameters:
                              </small>
                              <div className="d-flex flex-wrap gap-1">
                                {customizedHyperparameters.map((param, index) => (
                                  <Badge
                                    key={index}
                                    bg="warning"
                                    style={{
                                      fontSize: '0.75rem',
                                      padding: '0.4rem 0.75rem',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {param}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <InfoBox>
                              <small style={{ color: '#a0aec0', fontSize: '0.875rem' }}>
                                Using default hyperparameters
                              </small>
                            </InfoBox>
                          )
                        ) : (
                          <EmptyState accentColor="#ed8936">
                            <Settings
                              size={20}
                              style={{ color: '#ed8936', opacity: 0.6, marginBottom: '0.5rem' }}
                            />
                            <small style={{ color: '#a0aec0', fontSize: '0.875rem' }}>
                              Using default hyperparameters
                            </small>
                          </EmptyState>
                        )}
                      </div>
                    </SectionContent>
                  </Collapse>
                </SectionCard>

                {/* Training Controls */}
                <div
                  className="mt-auto pt-3"
                  style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}
                >
                  {isModelTraining ? (
                    <Button
                      variant="danger"
                      onClick={() => setShowStopTrainingDialog(true)}
                      disabled={isStoppingTraining}
                      className="w-100"
                      style={{
                        padding: '0.75rem',
                        fontWeight: '600',
                        borderRadius: '10px',
                        background: '#e53e3e',
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(229, 62, 62, 0.3)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {isStoppingTraining ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Stopping...
                        </>
                      ) : (
                        <>
                          <Square size={16} className="me-2" />
                          Stop Training
                        </>
                      )}
                    </Button>
                  ) : (
                    <>
                      <PrimaryButton
                        onClick={handleStartTraining}
                        disabled={!canStartTraining || isStartingTraining}
                        className="w-100 mb-2"
                      >
                        {isStartingTraining ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {localSelectedModelId ? 'Starting...' : 'Creating & Starting...'}
                          </>
                        ) : (
                          <>
                            <Play size={16} className="me-2" />
                            Start Training
                          </>
                        )}
                      </PrimaryButton>
                      {canFinishLater && (
                        <SecondaryButton
                          onClick={handleFinishLater}
                          disabled={isStartingTraining}
                          className="w-100 mb-2"
                          style={{ fontSize: '0.875rem' }}
                        >
                          {isStartingTraining ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Settings size={14} className="me-1" />
                              Finish Later
                            </>
                          )}
                        </SecondaryButton>
                      )}
                      <SecondaryButton
                        onClick={() => {
                          setLocalSelectedModelId(null)
                          setSelectedBaseModel(null)
                          setModelName('')
                          setHyperparameters(null)
                          dispatch(setTrainingSource([]))
                          dispatch(setValidationSource([]))
                        }}
                        className="w-100"
                        style={{ fontSize: '0.875rem' }}
                        disabled={isStartingTraining}
                      >
                        Reset Configuration
                      </SecondaryButton>
                    </>
                  )}
                </div>
              </Card.Body>
            </ConfigCard>
          </div>
        </Col>

        {/* Right Panel - Training Output/Progress */}
        <Col lg={8}>
          <OutputCard>
            <Card.Body className="d-flex flex-column p-4">
              <div className="mb-4">
                <h4
                  className="text-white mb-2"
                  style={{
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <BarChart3 size={24} />
                  Training Output
                </h4>
                <p
                  className="mb-0"
                  style={{ color: '#a0aec0', fontSize: '0.95rem', lineHeight: '1.5' }}
                >
                  {isModelTraining
                    ? `Monitor training progress for "${selectedModel?.name || 'your model'}"`
                    : 'Configure your training parameters and start training'}
                </p>
              </div>

              <div className="flex-grow-1">
                {isModelTraining && selectedModel ? (
                  // Show training progress when actively training
                  project?.annotation_type === 'classification' ? (
                    <ClassificationTrainingProgressCard
                      model={selectedModel}
                      gradient="linear-gradient(135deg, #402563, #2448cc)"
                    />
                  ) : (
                    <BBoxTrainingProgressCard
                      model={selectedModel}
                      gradient="linear-gradient(135deg, #402563, #2448cc)"
                    />
                  )
                ) : selectedModel?.state === 'TRAINED' && isModelTraining ? (
                  // Show completion state - but reset the page after showing briefly
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center">
                      <div
                        style={{
                          width: '80px',
                          height: '80px',
                          background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1.5rem',
                          boxShadow: '0 8px 25px rgba(72, 187, 120, 0.3)',
                        }}
                      >
                        <CheckCircle size={40} className="text-white" />
                      </div>
                      <h5
                        className="text-white mb-2"
                        style={{ fontWeight: '600', fontSize: '1.5rem' }}
                      >
                        Training Complete!
                      </h5>
                      <p className="mb-3" style={{ color: '#a0aec0', fontSize: '1rem' }}>
                        Your model "{selectedModel.name}" has been successfully trained.
                      </p>
                      <StatusBadge bg="success" className="px-4 py-2" style={{ fontSize: '1rem' }}>
                        Ready for Use
                      </StatusBadge>
                      <div className="mt-3">
                        <small style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                          Starting fresh training session...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : selectedBaseModel ? (
                  // Show base model selected and ready for training
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center">
                      <div
                        style={{
                          width: '80px',
                          height: '80px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1.5rem',
                          boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                        }}
                      >
                        <Brain size={40} className="text-white" />
                      </div>
                      <h5
                        className="text-white mb-2"
                        style={{ fontWeight: '600', fontSize: '1.5rem' }}
                      >
                        Ready to Train
                      </h5>
                      <p style={{ color: '#a0aec0', fontSize: '1rem', marginBottom: '1rem' }}>
                        "{selectedBaseModel.name}" base model selected. Configure datasets and start
                        training.
                      </p>
                      <StatusBadge bg="primary" className="px-4 py-2" style={{ fontSize: '1rem' }}>
                        Ready to Train
                      </StatusBadge>
                    </div>
                  </div>
                ) : (
                  // Show ready state when no model selected
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center">
                      <div
                        style={{
                          width: '80px',
                          height: '80px',
                          background: 'rgba(102, 126, 234, 0.1)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1.5rem',
                          border: '2px dashed rgba(102, 126, 234, 0.3)',
                        }}
                      >
                        <Brain size={40} style={{ color: '#667eea', opacity: 0.5 }} />
                      </div>
                      <h5
                        className="text-white mb-2"
                        style={{ fontWeight: '600', fontSize: '1.5rem' }}
                      >
                        Ready to Train
                      </h5>
                      <p style={{ color: '#a0aec0', fontSize: '1rem' }}>
                        Select a base model and training datasets to begin.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </OutputCard>
        </Col>
      </Row>

      {/* Modals */}
      <NewModelModal
        show={showBaseModelModal}
        onClose={() => {
          setShowBaseModelModal(false)
          setSelectedBaseModel(null)
          setFilterText('')
        }}
        baseModels={baseModels}
        filterText={filterText}
        setFilterText={setFilterText}
        selectedBaseModel={selectedBaseModel}
        onBaseModelSelect={handleBaseModelSelect}
        onGenerateNewModel={() => {
          setShowBaseModelModal(false)
        }}
      />

      <SelectDatasetModal
        show={showDatasetModal}
        onHide={() => setShowDatasetModal(false)}
        handleAcceptSelections={handleAcceptDatasetSelections}
        selectedSources={
          datasetModalType === 'training' ? selectedTrainingSources : selectedValidationSources
        }
        singleSelect={false}
        canCreate={true}
        updateGlobalState={false}
      />

      <HyperparameterSettingsModal
        show={showHyperparametersModal}
        handleClose={() => setShowHyperparametersModal(false)}
        onSave={(params) => {
          // Match old working code: modal returns params, store it directly
          setHyperparameters(params)
          setShowHyperparametersModal(false)
        }}
        initialHyperparameters={hyperparameters || selectedModel?.parameters?.hyperparameters}
        modelId={selectedModel?.id || localSelectedModelId || undefined}
        projectAnnotationType={project?.annotation_type}
      />

      <ConfirmDialog
        show={showStopTrainingDialog}
        onHide={() => setShowStopTrainingDialog(false)}
        title="Stop Training"
        message="Are you sure you want to stop the model training? This action cannot be undone."
        confirmRightText="Stop Training"
        cancelText="Cancel"
        onConfirmRight={handleStopTraining}
        variant="danger"
      />

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage || ''}
      />
    </TrainingContainer>
  )
}

export default TrainingView
